import { SharedModule } from '@/app/shared/shared.module';
import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { RegisterationSteps } from '@/app/auth/shared/auth-enums';
import { AuthService } from '@/app/auth/shared/services/auth.service';
import {
  BooleanResponseResult,
  CheckPhoneExistCommand,
  CountryViewModel,
  CreateOTPResultResponseResult,
  ErrorMessage,
  RegisterUserCompleteCommand,
  RequestPhoneOtpModel,
  UserProxy,
} from '@/app/proxy/proxies';
import { AppInitializerService } from '@/app/core/services/app-initializer.service';
import { extractBackendErrorMessage } from '@/app/auth/shared/auth-error.util';
import { findDefaultPhoneCountry } from '@/app/auth/shared/default-phone-country';
@Component({
  selector: 'auth-register-phone',
  imports: [SharedModule],
  templateUrl: './phone-number.component.html',
  styleUrl: './phone-number.component.less',
  providers: [UserProxy],
})
export class PhoneNumberComponent implements OnInit {
  @Output() steperChange = new EventEmitter<RegisterationSteps>();

  registerModal: RegisterUserCompleteCommand = new RegisterUserCompleteCommand();
  phoneNumber: number;
  selectedCountry: CountryViewModel | undefined;
  countries: CountryViewModel[] = [];

  somethingWentWrong: boolean = false;
  errorMessage: string = '';
  submitting: boolean = false;

  constructor(
    private _authService: AuthService,
    private _userProxy: UserProxy,
    private _applicationService: AppInitializerService
  ) {
    this.countries = this._applicationService.appStartupData?.countries ?? [];
    this.selectedCountry = findDefaultPhoneCountry(this.countries);
  }

  ngOnInit(): void {
    const registerModal = this._authService.getRegisterModal();
    if (!registerModal) {
      return;
    }

    this.registerModal = registerModal;
    this.restorePhoneEntry(registerModal);
  }

  /**
   * The step is destroyed when the user moves on, so coming back has to
   * refill the country and number from the saved modal.
   */
  private restorePhoneEntry(registerModal: RegisterUserCompleteCommand): void {
    if (registerModal.phone) {
      const digits = registerModal.phone.replace(/\D/g, '');
      if (digits) {
        this.phoneNumber = Number(digits);
      }
    }

    const savedCountry = registerModal.countryId
      ? this.countries.find((item) => item.id === registerModal.countryId)
      : undefined;
    if (savedCountry) {
      this.selectedCountry = savedCountry;
      return;
    }

    const savedCode = registerModal.phoneCode?.replace(/\+/g, '');
    if (!savedCode) {
      return;
    }

    const country = this.countries.find((item) => item.phoneCode?.replace(/\+/g, '') === savedCode);
    if (country) {
      this.selectedCountry = country;
    }
  }

  continuePhoneStep() {
    if (this.submitting) {
      return;
    }

    // The country dropdown can still be empty when the user hits continue.
    if (!this.phoneNumber || !this.selectedCountry?.phoneCode) {
      return;
    }

    let castedPhoneNumber = this.phoneNumber.toString().replace(/-/g, '');
    this.registerModal.phone = castedPhoneNumber;
    this.registerModal.phoneCode = this.selectedCountry.phoneCode.replace(/\+/g, '');
    this.registerModal.countryId = this.selectedCountry.id;
    // Consent is given by continuing past the terms notice on this screen.
    this.registerModal.acceptConsent = true;

    if (castedPhoneNumber && castedPhoneNumber.length >= 6 && !this.somethingWentWrong) {
      this.submitting = true;
      let input = new CheckPhoneExistCommand();
      input.phone = this.registerModal.phone;
      input.phoneCode = this.registerModal.phoneCode;
      this._userProxy.user_CheckPhoneExist(input).subscribe({
        next: (res: BooleanResponseResult) => {
          if (!res.result) {
            this.somethingWentWrong = false;
            this.errorMessage = '';
            this.requestPhoneOtp();
          } else {
            this.somethingWentWrong = true;
            this.errorMessage = 'doktar_app.register.phone_is_exist_error';
            this.submitting = false;
          }
        },
        error: () => {
          this.submitting = false;
        },
      });
    }
  }

  /**
   * Request the registration phone OTP. otpRequired true continues to the phone
   * code. otpRequired false means this country cannot receive SMS, so the phone
   * code is skipped and email is collected after the password.
   */
  private requestPhoneOtp() {
    this._userProxy
      .user_RequestPhoneOtp(
        new RequestPhoneOtpModel({
          phoneCode: this.registerModal.phoneCode,
          phone: this.registerModal.phone,
        })
      )
      .subscribe({
        next: (res: CreateOTPResultResponseResult) => {
          if (!res.isSuccess || !res.result) {
            this.handlePhoneOtpRequestFailure(res.errors);
            return;
          }

          const otpRequired = res.result.otpRequired;
          this._authService.setPhoneOtpRequired(otpRequired);
          if (otpRequired) {
            // A previous unsupported-country attempt must not keep its email.
            this.registerModal.email = undefined;
            this.registerModal.emailOtp = undefined;
            this.registerModal.phoneOtp = undefined;
          } else {
            // No phone code is sent. Email is collected after the password.
            this.registerModal.phoneOtp = null as unknown as number;
          }
          this._authService.setRegisterModal(this.registerModal);

          this.steperChange.emit(otpRequired ? RegisterationSteps.VERIFY_PHONE_OTP : RegisterationSteps.PASSWORD);
          this.submitting = false;
        },
        error: () => this.handlePhoneOtpRequestFailure(),
      });
  }

  private handlePhoneOtpRequestFailure(errors?: ErrorMessage[]) {
    this.submitting = false;
    this.somethingWentWrong = true;
    this.errorMessage = extractBackendErrorMessage(errors);
  }

  onPhoneNumberChange(_event: any) {
    if (this.somethingWentWrong) {
      this.somethingWentWrong = false;
      this.errorMessage = '';
    }
  }
}
